/**
 * Price Notification Detail — Review + Approve.
 * Version 1.1 · Sprint 1 · M1.
 *
 * The operator sees a per-product comparison:
 *
 *   Regular Gasoline
 *   Yesterday   1.2840
 *   Today       1.2340
 *   Difference  -5.0¢
 *
 * before pressing Approve. Approval reuses /api/rack/batch under the
 * hood so there is one authoritative rack-posting path.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle, ArrowRight, Warning, FileText, Info, Clock,
  Printer, PaperPlaneTilt,
} from "@phosphor-icons/react";
import { api, API } from "@/lib/api";
import { fmtCalendarDateShort } from "@/lib/calendarDate";
import EmailAuditEmbed from "@/components/EmailAuditEmbed";
import DailyPricingStatePipeline from "@/components/DailyPricingStatePipeline";

const INTERNAL_PRODUCT_LABEL = {
  regular: "Regular Gasoline",
  premium: "Premium Gasoline",
  clear_diesel: "Clear Diesel",
  dyed_diesel: "Dyed Diesel",
  def: "DEF",
};

export default function PriceNotificationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});          // { internal_code: {new_price} }
  const [approving, setApproving] = useState(false);
  const [unusualWarnings, setUnusualWarnings] = useState(null); // returned by backend when confirm needed
  // v1.1.1a · Two-step workflow (approve first, send second).
  const [sending, setSending] = useState(false);
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [resendConfirmOpen, setResendConfirmOpen] = useState(false);
  const [preview, setPreview] = useState(null);          // { subject, brief_date, recipients, ... }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deliveryConfirmation, setDeliveryConfirmation] = useState(null);
  // 2026-08-04 · Ref guard prevents rapid double-click from firing two POSTs
  // before the `sending` state update flushes on the next render. Without
  // this, StrictMode dev double-invocation or a fast operator can produce
  // duplicate emails despite the disabled attribute on the button.
  const sendInFlight = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/price-notifications/${id}`);
      setDoc(data);
      const seed = {};
      (data.mapped_products || []).forEach((p) => {
        seed[p.internal_product_code] = { new_price: p.new_price ?? "" };
      });
      setEdits(seed);
    } catch (e) {
      toast.error("Could not load notification");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const requiredMissing = useMemo(() => {
    if (!doc) return [];
    const missing = [];
    if (!doc.effective_date) missing.push("Effective date");
    (doc.mapped_products || []).forEach((p) => {
      const e = edits[p.internal_product_code];
      if (!e || e.new_price === "" || e.new_price == null || isNaN(Number(e.new_price))) {
        missing.push(`${INTERNAL_PRODUCT_LABEL[p.internal_product_code] || p.internal_product_code} price`);
      }
    });
    return missing;
  }, [doc, edits]);

  const canApprove = !approving && doc?.status === "parsed" && requiredMissing.length === 0;

  const submit = async (confirmUnusual = false) => {
    if (!doc) return;
    setApproving(true);
    setUnusualWarnings(null);
    try {
      const mapped_products = (doc.mapped_products || []).map((p) => {
        const edit = edits[p.internal_product_code] || {};
        return {
          internal_product_code: p.internal_product_code,
          supplier_code: p.supplier_code,
          supplier_description: p.supplier_description,
          old_price: p.old_price,
          new_price: edit.new_price ?? p.new_price,
          price_change: p.price_change,
        };
      });
      const { data } = await api.post(`/price-notifications/${id}/approve`, {
        mapped_products,
        confirm_unusual: confirmUnusual,
      });
      if (data.posted === false && data.requires_confirmation) {
        setUnusualWarnings(data.validation?.warnings || []);
        toast.info("Unusual price movement detected — please review and confirm.");
        return;
      }
      toast.success("Supplier rack prices approved. Ready to send driver notice when you are.");
      // v1.1.1a — approval no longer sends email. Communication is a separate
      // explicit action ("Send Driver Notice").
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Approval failed";
      toast.error(msg);
    } finally {
      setApproving(false);
    }
  };

  if (loading) return <div className="text-sm text-ink-secondary" data-testid="pn-detail-loading">Loading…</div>;
  if (!doc)    return <div className="text-sm text-ink-secondary">Not found.</div>;

  // ------------------------------------------------------------------
  // Two-step communication workflow (v1.1.1a — approval ≠ send).
  // ------------------------------------------------------------------
  const openSendConfirm = async () => {
    setSendConfirmOpen(true);
    setPreview(null);
    setPreviewLoading(true);
    try {
      const { data } = await api.get(`/price-notifications/${id}/recipient-preview`);
      setPreview(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Could not load recipient list";
      toast.error(msg);
      setSendConfirmOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmSend = async (isResend = false) => {
    // Synchronous idempotency guard — beats React state async lag.
    if (sendInFlight.current) return;
    sendInFlight.current = true;
    setSending(true);
    try {
      const { data } = await api.post(
        `/price-notifications/${id}/send-driver-notice`,
        isResend ? { resend: true } : {},
      );
      setSendConfirmOpen(false);
      setResendConfirmOpen(false);
      if (data?.driver_notice_delivery) {
        setDeliveryConfirmation({ ...data.driver_notice_delivery, is_resend: !!data?.is_resend });
      }
      if (data?.sent) {
        const label = data?.is_resend ? "resent" : "sent";
        toast.success(
          `Driver Price Notice ${label} successfully to ${data.driver_notice_delivery?.recipient_count ?? 0} qualified drivers.`
        );
      } else if ((data?.driver_notice_delivery?.recipient_count ?? 0) === 0) {
        toast.warning(data.driver_notice_delivery?.reason || "Nothing was sent — no qualified recipients.");
      } else {
        toast.error(data?.driver_notice_delivery?.error || "Send failed. Please retry.");
      }
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const kind = typeof detail === "object" ? detail?.kind : null;
      // Bug 2026-08-04 — server refused because notice was already sent.
      // Close the pre-send dialog and surface a helpful message.
      if (kind === "already_sent") {
        setSendConfirmOpen(false);
        toast.info(
          `Already sent to ${detail?.recipient_count ?? 0} driver(s) by ${detail?.sent_by || "someone"}. Use Resend if you truly need to send again.`
        );
        await load();
      } else if (kind === "distribution_blocked") {
        // STD-010 · P0 gate — pricing dataset invalid or incomplete.
        setSendConfirmOpen(false);
        setResendConfirmOpen(false);
        const missing = (detail?.missing_products || []).join(", ");
        const zero    = (detail?.zero_products    || []).join(", ");
        const parts = [];
        if (missing) parts.push(`Missing: ${missing}`);
        if (zero)    parts.push(`Zero or invalid: ${zero}`);
        toast.error(detail?.message || "Daily Pricing cannot be distributed.", {
          description: parts.join(" · ") || undefined,
          duration: 8000,
        });
      } else {
        const msg = typeof detail === "string" ? detail : detail?.message || "Send failed";
        toast.error(msg);
      }
    } finally {
      sendInFlight.current = false;
      setSending(false);
    }
  };

  const returnToChecklist = () => {
    setDeliveryConfirmation(null);
    nav("/start-the-day");
  };

  const approved = doc.status === "approved" || doc.status === "sent";
  const alreadySent = doc.status === "sent";

  return (
    <div className="space-y-6" data-testid="pn-detail-page">
      <button
        onClick={() => nav("/price-notifications")}
        data-testid="pn-detail-back"
        className="inline-flex items-center gap-1 text-sm text-ink-secondary hover:text-ink-primary"
      >
        <ArrowLeft size={14} /> All notifications
      </button>

      {/* Header */}
      <header className="rounded-lg border border-divider bg-surface p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-semibold text-ink-primary" data-testid="pn-detail-supplier">
              {doc.supplier}
            </h1>
            <div className="mt-1 text-sm text-ink-secondary">
              Effective <span data-testid="pn-detail-effective">{doc.effective_date}</span>
              {" · "}Contract {doc.contract_number || "—"} · Currency {doc.currency} {doc.unit}
            </div>
            {doc.pickup_locations?.length > 0 && (
              <div className="mt-1 text-xs text-ink-secondary">
                Terminals: {doc.pickup_locations.join(" · ")}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <a
              href={`${API}/price-notifications/${id}/pdf`}
              target="_blank"
              rel="noreferrer"
              data-testid="pn-detail-open-pdf"
              className="inline-flex items-center gap-1 rounded-md border border-divider bg-surface px-3 py-1.5 text-sm text-ink-primary hover:bg-surface-alt"
            >
              <FileText size={14} /> Original PDF
            </a>
            {alreadySent ? (
              <span
                data-testid="pn-detail-status-sent"
                className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"
              >
                <CheckCircle size={12} weight="fill" /> Sent
              </span>
            ) : approved ? (
              <span
                data-testid="pn-detail-status-approved"
                className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"
              >
                <CheckCircle size={12} weight="fill" /> Approved
              </span>
            ) : (
              <span
                data-testid="pn-detail-status-parsed"
                className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700"
              >
                <Clock size={12} weight="fill" /> Pending Review
              </span>
            )}
          </div>
        </div>

        {(doc.parser_issues || []).length > 0 && (
          <ul className="mt-4 space-y-1 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="pn-detail-issues">
            {doc.parser_issues.map((iss, idx) => (
              <li key={idx} className="flex items-start gap-1">
                <Warning size={12} weight="fill" className="mt-0.5" />
                <span>{iss.field}: {iss.message}</span>
              </li>
            ))}
          </ul>
        )}

        {(doc.unmapped_supplier_codes || []).length > 0 && (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700" data-testid="pn-detail-unmapped">
            <Info size={12} weight="fill" className="mt-0.5" />
            <span>
              These Suncor products were in the notification but are not tracked in your rack:
              {" "}
              <strong>{doc.unmapped_supplier_codes.join(", ")}</strong>. They will not be posted.
            </span>
          </div>
        )}
      </header>

      {/* Comparison table */}
      <section className="rounded-lg border border-divider bg-surface">
        <div className="border-b border-divider px-5 py-3">
          <h2 className="font-display text-base font-semibold text-ink-primary">Review changes</h2>
          <p className="text-xs text-ink-secondary">
            Yesterday's supplier price → today's supplier price. Edit any cell to override
            before approval. Approval posts to the daily rack and generates the Driver Price Notice.
          </p>
        </div>
        <table className="w-full text-sm" data-testid="pn-detail-comparison">
          <thead className="border-b border-divider bg-surface-alt text-left text-[11px] uppercase tracking-wide text-ink-secondary">
            <tr>
              <th className="px-5 py-2">Product</th>
              <th className="px-5 py-2 text-right">Yesterday</th>
              <th className="px-5 py-2 text-right">Today</th>
              <th className="px-5 py-2 text-right">Difference</th>
              <th className="px-5 py-2 text-right">Supplier code</th>
            </tr>
          </thead>
          <tbody>
            {(doc.mapped_products || []).map((p) => {
              const label = INTERNAL_PRODUCT_LABEL[p.internal_product_code] || p.internal_product_code;
              const edit = edits[p.internal_product_code] || {};
              const value = edit.new_price ?? p.new_price ?? "";
              const yesterday = Number(p.old_price);
              const today = Number(value);
              const delta = Number.isFinite(yesterday) && Number.isFinite(today) ? today - yesterday : null;
              const tone = delta === null ? "text-ink-secondary" : delta > 0 ? "text-rose-600" : delta < 0 ? "text-emerald-600" : "text-ink-secondary";
              return (
                <tr
                  key={p.internal_product_code}
                  className="border-b border-divider last:border-b-0"
                  data-testid={`pn-detail-row-${p.internal_product_code}`}
                >
                  <td className="px-5 py-3 font-semibold text-ink-primary">
                    <div>{label}</div>
                    <div className="text-[11px] font-normal text-ink-secondary">{p.supplier_description}</div>
                  </td>
                  <td className="px-5 py-3 text-right font-mono text-ink-primary" data-testid={`pn-detail-old-${p.internal_product_code}`}>
                    {p.old_price ?? "—"}
                  </td>
                  <td className="px-5 py-3 text-right">
                    {approved ? (
                      <span className="font-mono text-ink-primary" data-testid={`pn-detail-new-${p.internal_product_code}`}>
                        {value}
                      </span>
                    ) : (
                      <input
                        data-testid={`pn-detail-input-${p.internal_product_code}`}
                        value={value}
                        onChange={(e) => setEdits((prev) => ({
                          ...prev,
                          [p.internal_product_code]: { new_price: e.target.value },
                        }))}
                        className="w-24 rounded-md border border-divider bg-surface px-2 py-1 text-right font-mono text-ink-primary focus:border-brand focus:outline-none"
                        inputMode="decimal"
                      />
                    )}
                  </td>
                  <td className={`px-5 py-3 text-right font-mono ${tone}`} data-testid={`pn-detail-delta-${p.internal_product_code}`}>
                    {delta === null ? "—" :
                      `${delta > 0 ? "+" : delta < 0 ? "-" : ""}${Math.abs(delta).toFixed(4)}`}
                  </td>
                  <td className="px-5 py-3 text-right font-mono text-[11px] text-ink-secondary">
                    {p.supplier_code}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      {/* Unusual movement confirmation */}
      {unusualWarnings && unusualWarnings.length > 0 && (
        <section
          data-testid="pn-detail-unusual"
          className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
        >
          <div className="mb-2 flex items-center gap-1 font-semibold">
            <Warning size={14} weight="fill" /> Unusual price movement detected
          </div>
          <ul className="mb-3 space-y-1">
            {unusualWarnings.map((w, i) => (
              <li key={i} className="font-mono text-xs">{w.message}</li>
            ))}
          </ul>
          <button
            data-testid="pn-detail-confirm-unusual"
            onClick={() => submit(true)}
            disabled={approving}
            className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
          >
            Confirm and post anyway
          </button>
        </section>
      )}

      {/* Approve strip — Step 1 · Approval */}
      {!approved ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-divider bg-surface-alt p-4">
          <div className="text-xs text-ink-secondary" data-testid="pn-detail-missing-summary">
            {requiredMissing.length === 0
              ? "All required fields extracted. Ready to approve."
              : `Missing: ${requiredMissing.join(", ")}`}
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="pn-detail-approve"
              onClick={() => submit(false)}
              disabled={!canApprove}
              className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-brand-foreground shadow-token-sm hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              <CheckCircle size={16} weight="bold" />
              {approving ? "Posting…" : "Approve Notice"}
            </button>
            <button
              data-testid="pn-detail-send-disabled"
              disabled
              title="Approve the notice first"
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-400 cursor-not-allowed"
            >
              <PaperPlaneTilt size={16} weight="bold" /> Send Driver Notice
            </button>
          </div>
        </div>
      ) : (
        /* Post-approval strip — audit + Step 2 · Send Driver Notice */
        <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
          <div className="space-y-1 text-sm text-emerald-900">
            <div data-testid="pn-detail-approved-line" className="flex items-center gap-1.5">
              <CheckCircle size={14} weight="fill" className="text-emerald-600" />
              <span>
                <strong>Approved</strong> by {doc.approved_by} at{" "}
                {doc.approved_at ? new Date(doc.approved_at).toLocaleString() : "—"}
              </span>
            </div>
            {alreadySent && (
              <div data-testid="pn-detail-sent-line" className="flex items-center gap-1.5">
                <CheckCircle size={14} weight="fill" className="text-emerald-600" />
                <span>
                  <strong>
                    Sent to {(doc.driver_notice_delivery?.recipient_count ?? 0)}{" "}
                    driver{(doc.driver_notice_delivery?.recipient_count ?? 0) === 1 ? "" : "s"}
                  </strong>{" "}
                  by {doc.sent_by} at {doc.sent_at ? new Date(doc.sent_at).toLocaleString() : "—"}
                </span>
              </div>
            )}
            <div className="text-[11px] text-emerald-700 opacity-80">
              Posting ID <span className="font-mono">{doc.posting_id}</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              data-testid="pn-detail-open-driver-notice"
              onClick={() => nav(`/driver-price-notice/${doc.effective_date}`)}
              className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-50"
            >
              <Printer size={12} weight="bold" /> Open Driver Notice
            </button>
            {!alreadySent ? (
              <button
                data-testid="pn-detail-send-driver-notice"
                onClick={openSendConfirm}
                disabled={sending}
                className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-brand-foreground shadow-token-sm hover:bg-brand-hover disabled:opacity-50"
              >
                <PaperPlaneTilt size={14} weight="bold" />
                {sending ? "Sending…" : "Send Driver Notice"} <ArrowRight size={14} />
              </button>
            ) : (
              <>
                <button
                  data-testid="pn-detail-resend-driver-notice"
                  onClick={() => setResendConfirmOpen(true)}
                  disabled={sending}
                  className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-50 disabled:opacity-50"
                  title="Resend the Driver Price Notice (already sent once)"
                >
                  <PaperPlaneTilt size={12} weight="bold" /> Resend Driver Notice
                </button>
                <button
                  data-testid="pn-detail-return-checklist"
                  onClick={() => nav("/start-the-day")}
                  className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-brand-foreground hover:bg-brand-hover"
                >
                  Return to Start the Day <ArrowRight size={14} />
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {doc?.effective_date && (
        <EmailAuditEmbed
          workflowPrefix={`driver_price_notice:${doc.effective_date}:`}
          title="Driver price notice email activity"
        />
      )}

      {id && <DailyPricingStatePipeline notificationId={id} />}

      {/* Send confirmation dialog (Step 2 pre-flight) */}
      {sendConfirmOpen && (
        <SendConfirmDialog
          preview={preview}
          loading={previewLoading}
          sending={sending}
          onCancel={() => setSendConfirmOpen(false)}
          onConfirm={() => confirmSend(false)}
        />
      )}

      {/* Resend confirmation — distinct from initial-send to prevent accidents */}
      {resendConfirmOpen && (
        <ResendConfirmDialog
          previousDelivery={doc.driver_notice_delivery}
          sending={sending}
          onCancel={() => setResendConfirmOpen(false)}
          onConfirm={() => confirmSend(true)}
        />
      )}

      {deliveryConfirmation && (
        <DeliveryConfirmationDialog
          delivery={deliveryConfirmation}
          effectiveDate={doc.effective_date}
          onOpenDriverNotice={() => nav(`/driver-price-notice/${doc.effective_date}`)}
          onCopyLink={async () => {
            try {
              const publicUrl = `${window.location.origin}/driver-price-notice/${doc.effective_date}`;
              await navigator.clipboard.writeText(publicUrl);
              toast.success("Link copied to clipboard.");
            } catch (e) {
              toast.error("Could not copy link.");
            }
          }}
          onReturnToChecklist={returnToChecklist}
          onClose={returnToChecklist}
        />
      )}
    </div>
  );
}


/**
 * DeliveryConfirmationDialog — STD-009 compliant delivery summary.
 *
 * Answers the four operator questions the moment the send completes:
 *   1. What happened?          → "Driver Price Notice Sent"
 *   2. Did it succeed?         → sent count + green/rose tone
 *   3. Who received it?        → named list, source pill
 *   4. What happens next?      → Return to Start the Day (primary)
 */
function DeliveryConfirmationDialog({
  delivery,
  effectiveDate,
  onOpenDriverNotice,
  onCopyLink,
  onReturnToChecklist,
  onClose,
}) {
  const sent = !!delivery?.sent;
  const count = delivery?.recipient_count ?? 0;
  const usedDispatch = !!delivery?.used_dispatch;
  const recipients = delivery?.recipients || [];
  const reason = delivery?.reason || "";
  const errorMsg = delivery?.error || "";

  const overallTone = sent
    ? { border: "border-emerald-200", bg: "bg-emerald-50",  text: "text-emerald-800", pill: "bg-emerald-600" }
    : count === 0
      ? { border: "border-amber-200",   bg: "bg-amber-50",   text: "text-amber-900",   pill: "bg-amber-600" }
      : { border: "border-rose-200",    bg: "bg-rose-50",    text: "text-rose-900",    pill: "bg-rose-600" };

  const headline = sent
    ? "Driver Price Notice Sent"
    : count === 0
      ? "Nothing Was Sent"
      : "Send Failed";

  return (
    <div
      data-testid="pn-delivery-confirmation"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="max-w-md w-full rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`border-b ${overallTone.border} ${overallTone.bg} px-5 py-4 rounded-t-lg`}>
          <div className={`flex items-center gap-2 ${overallTone.text}`}>
            <CheckCircle size={20} weight="fill" />
            <h2 className="text-base font-semibold" data-testid="pn-delivery-title">
              {headline}
            </h2>
          </div>
          {sent && (
            <p className={`mt-1 text-xs ${overallTone.text} opacity-80`}>
              Returning to the Start of Day Checklist…
            </p>
          )}
        </div>

        {/* Delivery summary */}
        <div className="px-5 py-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Driver Price Notice
            </div>
            <span
              data-testid="pn-delivery-status-pill"
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white ${overallTone.pill}`}
            >
              {sent ? `${count} sent` : count === 0 ? "Not sent" : "Failed"}
            </span>
          </div>

          {sent && (
            <p data-testid="pn-delivery-sent-line" className="text-sm text-gray-700">
              Email sent to <strong>{count}</strong> driver{count === 1 ? "" : "s"}
              {usedDispatch ? " (assigned to today's dispatch)" : " (all fuel-qualified drivers)"}.
            </p>
          )}
          {!sent && count === 0 && (
            <p data-testid="pn-delivery-nosend-line" className="text-sm text-amber-800">
              {reason || "Nothing was sent."}
            </p>
          )}
          {!sent && count > 0 && (
            <p data-testid="pn-delivery-error-line" className="text-sm text-rose-800">
              Delivery failed: {errorMsg || "Please retry."}
            </p>
          )}

          {recipients.length > 0 && (
            <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
                Recipients
              </div>
              <ul data-testid="pn-delivery-recipients" className="space-y-0.5">
                {recipients.map((r) => (
                  <li key={r.email} data-testid={`pn-delivery-recipient-${r.email}`}
                      className="text-sm text-gray-800 flex items-center gap-2">
                    <span className="font-medium">{r.name}</span>
                    <span className="text-xs text-gray-500 font-mono">{r.email}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="border-t border-gray-200 px-5 py-3 flex flex-wrap items-center justify-end gap-2 rounded-b-lg">
          <button
            data-testid="pn-delivery-copy-link"
            onClick={onCopyLink}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            Copy Link
          </button>
          <button
            data-testid="pn-delivery-open-notice"
            onClick={onOpenDriverNotice}
            className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            <Printer size={12} weight="bold" /> Open Driver Notice
          </button>
          <button
            data-testid="pn-delivery-return-checklist"
            onClick={onReturnToChecklist || onClose}
            className="inline-flex items-center gap-1 rounded-md bg-brand px-4 py-1.5 text-xs font-semibold text-brand-foreground hover:bg-brand-hover"
          >
            Return to Start of Day <ArrowRight size={12} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * SendConfirmDialog — pre-flight confirmation before sending.
 *
 * Shows the effective date, subject line, resolved recipient list, and
 * the "who" (dispatch subset vs broadcast) so operations knows exactly
 * what will land in drivers' inboxes.
 */
function SendConfirmDialog({ preview, loading, sending, onCancel, onConfirm }) {
  const recipients = preview?.recipients || [];
  const count = preview?.recipient_count ?? 0;
  const usedDispatch = !!preview?.used_dispatch;

  return (
    <div
      data-testid="pn-send-confirm"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={sending ? undefined : onCancel}
    >
      <div
        className="max-w-md w-full rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-gray-200 px-5 py-4 rounded-t-lg">
          <div className="flex items-center gap-2 text-gray-900">
            <PaperPlaneTilt size={18} weight="bold" />
            <h2 className="text-base font-semibold" data-testid="pn-send-confirm-title">
              Send Driver Price Notice?
            </h2>
          </div>
        </div>

        <div className="px-5 py-4 space-y-4 text-sm">
          {loading && (
            <p data-testid="pn-send-confirm-loading" className="text-xs text-gray-500">
              Resolving recipients…
            </p>
          )}
          {!loading && preview && (
            <>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  Effective Date
                </div>
                <div data-testid="pn-send-confirm-effective" className="text-sm text-gray-900">
                  {preview.brief_date}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  Subject
                </div>
                <div data-testid="pn-send-confirm-subject" className="text-sm text-gray-900">
                  {preview.subject}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  Recipients
                </div>
                <div data-testid="pn-send-confirm-count" className="text-sm text-gray-900">
                  <strong>{count}</strong> qualified driver{count === 1 ? "" : "s"}
                  {usedDispatch ? " (today's dispatch)" : " (all fuel-qualified)"}
                </div>
                {recipients.length > 0 && (
                  <ul className="mt-1.5 space-y-0.5 rounded border border-gray-200 bg-gray-50 px-3 py-2">
                    {recipients.map((r) => (
                      <li key={r.email}
                          data-testid={`pn-send-confirm-recipient-${r.email}`}
                          className="text-xs text-gray-800 flex items-center gap-2">
                        <span className="font-medium">{r.name}</span>
                        <span className="text-[10px] text-gray-500 font-mono">{r.email}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {count === 0 && (
                  <p data-testid="pn-send-confirm-empty" className="mt-1.5 text-xs text-amber-800">
                    {preview.reason}
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        <div className="border-t border-gray-200 px-5 py-3 flex items-center justify-end gap-2 rounded-b-lg">
          <button
            data-testid="pn-send-confirm-cancel"
            onClick={onCancel}
            disabled={sending}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            data-testid="pn-send-confirm-send"
            onClick={onConfirm}
            disabled={sending || loading || count === 0}
            className="inline-flex items-center gap-1 rounded-md bg-brand px-4 py-1.5 text-xs font-semibold text-brand-foreground hover:bg-brand-hover disabled:opacity-50"
          >
            <PaperPlaneTilt size={12} weight="bold" />
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * ResendConfirmDialog — high-friction confirmation for opt-in resend.
 *
 * Explicitly separated from the initial-send dialog to prevent
 * accidental duplicate emails. Copy is deliberately assertive: the
 * operator must acknowledge the notice was already sent.
 */
function ResendConfirmDialog({ previousDelivery, sending, onCancel, onConfirm }) {
  const count = previousDelivery?.recipient_count ?? 0;
  return (
    <div
      data-testid="pn-resend-confirm"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={sending ? undefined : onCancel}
    >
      <div
        className="max-w-md w-full rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-amber-200 bg-amber-50 px-5 py-4 rounded-t-lg">
          <div className="flex items-center gap-2 text-amber-900">
            <Warning size={18} weight="fill" />
            <h2 className="text-base font-semibold" data-testid="pn-resend-confirm-title">
              Resend Driver Price Notice?
            </h2>
          </div>
        </div>
        <div className="px-5 py-4 space-y-3 text-sm">
          <p data-testid="pn-resend-confirm-body" className="text-gray-800">
            This notice has already been sent to <strong>{count}</strong> driver
            {count === 1 ? "" : "s"}. Do you want to send it again?
          </p>
          <p className="text-xs text-amber-800">
            A resend delivers a fresh email to every qualified driver. Only continue if
            operations has decided a redistribution is required.
          </p>
        </div>
        <div className="border-t border-gray-200 px-5 py-3 flex items-center justify-end gap-2 rounded-b-lg">
          <button
            data-testid="pn-resend-confirm-cancel"
            onClick={onCancel}
            disabled={sending}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            data-testid="pn-resend-confirm-send"
            onClick={onConfirm}
            disabled={sending}
            className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
          >
            <PaperPlaneTilt size={12} weight="bold" />
            {sending ? "Resending…" : "Resend Now"}
          </button>
        </div>
      </div>
    </div>
  );
}

